// bookie-copper-preview — bk06 "Mis Tafti" card-preview.
//
// A copper-foiled birthday taklifnoma. The outer frame is a hammered
// copper gradient, inside sits an ivory card with a copper hairline,
// and a circular "35" medallion (the age numeral) anchors the centre
// as the focal element. Names + date + venue follow the rose-gold
// pattern verbatim so the catalog reads as a single family.
import { Photo, type LayoutCtx } from "./_shared";

export function BookieCopperPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const FOIL = `linear-gradient(135deg, ${accent} 0%, #E6B886 22%, #F5E0C2 45%, #E6B886 65%, ${accent} 100%)`;

  // The mock `names` for this template carries "Celebrant · Age" — split
  // so we render the celebrant in the typographic line below the disc.
  const celebrant = (names.split(/\s*[·&,]\s*/)[0] || names).trim();
  const ageMatch = names.match(/(\d{2,3})/);
  const age = ageMatch ? ageMatch[1] : "35";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Copper "age" medallion — disc with the numeral inside. */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                  boxShadow: `inset 0 0 0 1px ${accent}66`,
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 45%, ${accent}33, ${bg})`,
                      }}
                    />
                  }
                />
                {/* Age numeral printed on top, in the template font. */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    fontFamily: font,
                    fontSize: 64,
                    fontWeight: 500,
                    color: accent,
                    lineHeight: 1,
                    textShadow: `2px 2px 0 ${bg}`,
                  }}
                >
                  {age}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Celebrant name */}
        <div
          style={{
            fontFamily: font,
            fontSize: 28,
            fontWeight: 500,
            lineHeight: 1.05,
            color: ink,
          }}
        >
          {celebrant}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TUG'ILGAN KUN · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
